import { InternalAccountType } from "@wingspanhq/bookkeeping/dist/lib/interfaces/internalAccount";
import { PendingTransactionSubType } from "@wingspanhq/bookkeeping/dist/lib/interfaces/pendingTransaction";
import {
  useModalContext,
  WSAmountInput,
  WSButton,
  WSButtons,
  WSForm,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { useCreateBankingPendingTransaction } from "../../query/bookkeeping/mutations";
import { useUserId } from "../../query/hooks/helpers";
import { usePayoutSettings } from "../../query/payments/queries";
import { useAccounts } from "../../query/users/queries";
import { getPayoutAccount } from "../../query/users/selectors";
import { WSQueries } from "../../query/WSQuery";
import { CurrencyUSD } from "../../services/bookkeeping";

export const ADD_FUNDS_MODAL = "ADD_FUNDS_MODAL";

export const AddFundsModal: React.FC = () => {
  const { closeModal } = useModalContext();
  const userId = useUserId();
  const history = useHistory();
  const qAccounts = useAccounts();
  const payoutSettingsQuery = usePayoutSettings(userId);
  const [
    createTransaction,
    metaCreateTransaction
  ] = useCreateBankingPendingTransaction();

  return (
    <WSModal
      name={ADD_FUNDS_MODAL}
      size="S"
      onClose={() => closeModal(ADD_FUNDS_MODAL)}
    >
      <WSQueries queries={{ qAccounts, payoutSettingsQuery }}>
        {({
          qAccounts: { data: accounts },
          payoutSettingsQuery: { data: payoutSettings }
        }) => {
          const account = getPayoutAccount(payoutSettings, accounts);

          return (
            <>
              <WSText.Heading2 mb="M">Add Funds</WSText.Heading2>

              <WSText.Paragraph mb="L">
                Deposit will be made from
                {account?.mask && account.institution ? (
                  <>
                    {" your payout account "}
                    <b>{`${account.institution} ${account.subType} (${account?.mask}) `}</b>
                  </>
                ) : (
                  " your bank account "
                )}
                and should appear in 3-5 business days.{" "}
                <WSButton.Link
                  type="button"
                  inline
                  onClick={() =>
                    history.push("/member/settings/payment-methods")
                  }
                >
                  Update payments account.
                </WSButton.Link>
              </WSText.Paragraph>

              <WSForm
                defaultValues={{
                  amount: ""
                }}
                validationSchema={Yup.object().shape({
                  amount: Yup.string().required("Amount is required")
                })}
                onSubmit={async formData => {
                  await createTransaction(
                    {
                      amount: parseFloat(formData.amount) || 0,
                      currency: CurrencyUSD,
                      subType: PendingTransactionSubType.Deposit,
                      type: InternalAccountType.Banking
                    },
                    {
                      throwOnError: true
                    }
                  );

                  closeModal(ADD_FUNDS_MODAL);
                }}
              >
                {({ setValue, getValues }) => (
                  <>
                    <WSForm.Field
                      mb="M"
                      label="Dollar amount"
                      name="amount"
                      component={WSAmountInput}
                      componentProps={{
                        prefix: "$",
                        allowNegative: false,
                        onBlur: () =>
                          setValue(
                            "amount",
                            getValues("amount") === ""
                              ? ""
                              : Number(getValues("amount")).toFixed(2)
                          )
                      }}
                    />

                    <WSErrorMessage
                      mt="M"
                      error={metaCreateTransaction.error}
                      contextKey="PendingWithholding"
                    />

                    <WSButtons mt="XL" forceFullWidth>
                      <WSForm.SubmitButton data-testid="submit">
                        Add
                      </WSForm.SubmitButton>
                      <WSButton.Tertiary
                        onClick={() => closeModal(ADD_FUNDS_MODAL)}
                      >
                        Cancel
                      </WSButton.Tertiary>
                    </WSButtons>
                  </>
                )}
              </WSForm>
            </>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};
